import {
  CatalogSearchMode,
  selectIsCatalogMode,
  useCatalogSearchStore,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/stores/catalog-search-store';
import { OrderEntryMode } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/types';
import { OrderEntryPriceEditDisplayHeader } from '@/types';
import { SalesRepConfig } from '@domain/configuration/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCatalogSearchLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-catalog-search-loader';
import { useDebouncedCallback } from 'use-debounce';

const CATALOG_SEARCH_DEBOUNCE_MS = 500;

interface UseOrderEntryCatalogSearchParams {
  deliveryDate: string;
  mode: OrderEntryMode;
  orderGuideUUID: string;
  priceEditDisplayHeaders: OrderEntryPriceEditDisplayHeader[];
  restaurantUUID: string;
  salesRepConfig: SalesRepConfig | null;
  supplierUUID: string;
}

/**
 * Filters the guide as the user types, and on request runs a full catalog
 * search that replaces the guide until cleared.
 *
 * @param params.mode - A Build/Review switch clears the search.
 * @param params.orderGuideUUID - Results are scoped to one guide; a change
 * clears the search.
 */
export const useOrderEntryCatalogSearch = ({
  deliveryDate,
  mode,
  orderGuideUUID,
  priceEditDisplayHeaders,
  restaurantUUID,
  salesRepConfig,
  supplierUUID,
}: UseOrderEntryCatalogSearchParams) => {
  const [searchTerm, setSearchTerm] = useState('');

  const catalogMode = useCatalogSearchStore((state) => state.mode);
  const catalogResults = useCatalogSearchStore((state) => state.results);
  const catalogSearchTerm = useCatalogSearchStore((state) => state.searchTerm);
  const catalogErrorCode = useCatalogSearchStore((state) => state.errorCode);
  const onExitCatalogMode = useCatalogSearchStore(
    (state) => state.onExitCatalogMode,
  );
  const isInCatalogMode = useCatalogSearchStore(selectIsCatalogMode);

  const { cancelCatalogSearch, executeCatalogSearch } = useCatalogSearchLoader({
    deliveryDate,
    priceEditDisplayHeaders,
    restaurantUUID,
    salesRepConfig,
    supplierUUID,
  });

  const executeCatalogSearchRef = useRef(executeCatalogSearch);
  executeCatalogSearchRef.current = executeCatalogSearch;

  const runCatalogSearch = useCallback((term: string) => {
    void executeCatalogSearchRef.current(term);
  }, []);

  const debouncedCatalogSearch = useDebouncedCallback((text: string) => {
    if (!isInCatalogMode) {
      return;
    }

    if (!text.trim()) {
      resetCatalogSearch();
      return;
    }

    if (text.trim() !== catalogSearchTerm) {
      runCatalogSearch(text.trim());
    }
  }, CATALOG_SEARCH_DEBOUNCE_MS);

  const resetCatalogSearch = useCallback(() => {
    debouncedCatalogSearch.cancel();
    cancelCatalogSearch();
    onExitCatalogMode();
  }, [debouncedCatalogSearch, cancelCatalogSearch, onExitCatalogMode]);

  const handleSearchChange = useCallback(
    (text: string) => {
      setSearchTerm(text);
      if (!text.trim()) {
        resetCatalogSearch();
        return;
      }
      debouncedCatalogSearch(text);
    },
    [debouncedCatalogSearch, resetCatalogSearch],
  );

  const handleCatalogSearch = useCallback(
    (text: string) => {
      if (!text.trim()) {
        return;
      }
      runCatalogSearch(text.trim());
    },
    [runCatalogSearch],
  );

  const handleRetryCatalogSearch = useCallback(() => {
    if (catalogSearchTerm) {
      runCatalogSearch(catalogSearchTerm);
    }
  }, [catalogSearchTerm, runCatalogSearch]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    resetCatalogSearch();
  }, [resetCatalogSearch]);

  useEffect(
    () => clearSearch,
    [
      deliveryDate,
      mode,
      orderGuideUUID,
      restaurantUUID,
      supplierUUID,
      clearSearch,
    ],
  );

  return {
    catalogErrorCode,
    catalogResults,
    catalogSearchTerm,
    handleCatalogSearch,
    handleRetryCatalogSearch,
    handleSearchChange,
    isCatalogSearching: catalogMode === CatalogSearchMode.CatalogSearching,
    isInCatalogMode,
    searchTerm,
    showCatalogEmpty: catalogMode === CatalogSearchMode.CatalogEmpty,
    showCatalogError: catalogMode === CatalogSearchMode.CatalogError,
    showCatalogResults: catalogMode === CatalogSearchMode.CatalogResults,
  };
};

export type OrderEntryCatalogSearch = ReturnType<
  typeof useOrderEntryCatalogSearch
>;
